import { Chip } from '@mui/material';
import {
  find,
  isEmpty,
  isFunction,
  isNumber,
  isString,
  map,
  transform,
} from 'lodash';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterDefinitionType } from 'enums';
import {
  FilterDefinition,
  FilterDefinitionMerchant,
  FilterDefinitionTrader,
  FilterDefinitionUser,
  Merchant,
  Trader,
} from 'types';
import { formatUtils } from 'utils';

type FilterChip = {
  label: string;
  value: any;
  name: string;
  removable?: boolean;
};

type Props = {
  definitions: FilterDefinition[];
  filters: any;
  onDelete: (name: string) => void;
};

export const FiltersChips: React.FC<Props> = ({
  definitions,
  filters,
  onDelete,
}) => {
  const { t } = useTranslation();

  const getUserFromFilterDefinition = useCallback(
    (
      definition:
        | FilterDefinitionUser<any>
        | FilterDefinitionTrader<any>
        | FilterDefinitionMerchant<any>,
      value: string,
    ) =>
      definition.type === FilterDefinitionType.User
        ? find(definition.users, { id: value })
        : find<Trader | Merchant>(
            (definition as FilterDefinitionTrader<any>).traders ||
              (definition as FilterDefinitionMerchant<any>).merchants,
            {
              id: value,
            },
          )?.user,
    [],
  );

  const chips = useMemo(
    () =>
      transform(
        definitions,
        (acc, definition) => {
          if (definition.type === FilterDefinitionType.Range) {
            const minValue = filters?.[definition.minName] as number;
            const maxValue = filters?.[definition.maxName] as number;

            if (!isEmpty(minValue) || isNumber(minValue)) {
              acc.push({
                label: `${definition.label} ${t(
                  'filters.from',
                ).toLowerCase()}: ${minValue}`,
                value: minValue,
                name: definition.minName,
                removable: definition.removable !== false,
              });
            }
            if (!isEmpty(maxValue) || isNumber(maxValue)) {
              acc.push({
                label: `${definition.label} ${t(
                  'filters.to',
                ).toLowerCase()}: ${maxValue}`,
                value: maxValue,
                name: definition.maxName,
                removable: definition.removable !== false,
              });
            }
          } else if (
            definition.type === FilterDefinitionType.DateRange ||
            definition.type === FilterDefinitionType.DateTimeRange
          ) {
            const fromName = definition.from?.name;
            const toName = definition.to?.name;

            const fromValue = filters?.[fromName] as string;
            const toValue = filters?.[toName] as string;

            if (isString(fromValue)) {
              acc.push({
                label: `${definition.from.label}: ${formatUtils.formatDate(
                  fromValue,
                )}`,
                value: fromValue,
                name: fromName,
                removable: definition.removable !== false,
              });
            }
            if (isString(toValue)) {
              acc.push({
                label: `${definition.to.label}: ${formatUtils.formatDate(
                  toValue,
                )}`,
                value: toValue,
                name: toName,
                removable: definition.removable !== false,
              });
            }
          } else if (
            definition.type === FilterDefinitionType.User ||
            definition.type === FilterDefinitionType.Trader ||
            definition.type === FilterDefinitionType.Merchant
          ) {
            const value = filters?.[definition.name];
            if (!isEmpty(value)) {
              let displayName = value;
              if (isFunction(definition.getDisplayName)) {
                displayName = definition.getDisplayName(value) || value;
              } else {
                const user = getUserFromFilterDefinition(definition, value);
                if (user) {
                  displayName = user.name || user.email || value;
                }
              }

              acc.push({
                label: `${definition.label}: ${displayName}`,
                value,
                name: definition.name,
                removable: definition.removable !== false,
              });
            }
          } else if (definition.type === FilterDefinitionType.Shop) {
            const value = filters?.[definition.name];
            if (!isEmpty(value)) {
              let displayName = value;
              if (isFunction(definition.getDisplayName)) {
                displayName = definition.getDisplayName(value) || value;
              } else {
                displayName =
                  find(definition.shops, { id: value })?.name || value;
              }

              acc.push({
                label: `${definition.label}: ${displayName}`,
                value,
                name: definition.name,
                removable: definition.removable !== false,
              });
            }
          } else if (definition.type === FilterDefinitionType.TextArea) {
            const value = filters?.[definition.name] as string;
            if (!isEmpty(value)) {
              const ids = value
                .trim()
                .split(/[\n,\s]+/)
                .filter(Boolean);
              const displayName =
                ids.length > 1 ? `${ids.length} ${t('filters.items')}` : ids[0];
              acc.push({
                label: `${definition.label}: ${displayName}`,
                value,
                name: definition.name,
                removable: definition.removable !== false,
              });
            }
          } else {
            const value = filters?.[definition.name];

            if (!isEmpty(value) || isNumber(value)) {
              const displayName = definition.getDisplayName?.(value) || value;
              acc.push({
                label: `${definition.label}: ${displayName}`,
                value,
                name: definition.name,
                removable: definition.removable !== false,
              });
            }
          }
        },
        [] as FilterChip[],
      ),
    [definitions, filters, t, getUserFromFilterDefinition],
  );
  return (
    <Fragment>
      {map(chips, (chip) => (
        <Chip
          sx={{
            marginRight: 2,
            marginBottom: 2,
            // added support for multiline chip: https://mui.com/material-ui/react-chip/#multiline-chip
            height: 'auto',
            minHeight: '32px',
            padding: '4px 0',
            wordBreak: 'break-all',
            '& .MuiChip-label': {
              display: 'block',
              whiteSpace: 'normal',
            },
          }}
          key={chip.label}
          label={chip.label}
          variant="outlined"
          onDelete={chip.removable ? () => onDelete(chip.name) : undefined}
        />
      ))}
    </Fragment>
  );
};
